/**
 * Generic Multi-Model Data Hook Factory
 *
 * Creates hooks for fetching data for multiple models with:
 * - Per-model caching (add/remove doesn't refetch everything)
 * - IndexedDB persistence for worker access
 * - Request cancellation when models are deselected
 * - Debounced batch prefetching for rapid selections
 */
import {
  ModelDataType,
  readMultipleModelData,
  writeModelData,
  writeMultipleModelData,
} from '@/types/storage/modelDataStorage';
import { useQueries, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface MultiModelDataResult<T> {
  /** Combined data from all models, keyed by modelId */
  data: Record<string, T | null>;
  /** True if any model data is currently being loaded (initial load) */
  isLoading: boolean;
  /** True if any model data is being fetched (including refetches) */
  isFetching: boolean;
  /** True if all models have been successfully loaded */
  isSuccess: boolean;
  /** Array of any errors that occurred */
  errors: (Error | null)[];
  /** Individual query results for advanced use cases */
  queries: UseQueryResult<T | null, Error>[];
  /** Refetch data for all models */
  refetchAll: () => Promise<void>;
  /** Refetch data for a specific model */
  refetchModel: (modelId: string) => Promise<void>;
  /** Invalidate and refetch a model's data */
  invalidateModel: (modelId: string) => Promise<void>;
}

export interface UseMultiModelDataConfig<T> {
  /** 
   * Data type for IndexedDB cache key namespacing.
   * If undefined, IDB caching is disabled (useful when batchFetchFn handles IDB separately).
   */
  dataType?: ModelDataType;
  /** React Query key prefix (e.g., 'nodeSupplementals') */
  queryKeyPrefix: string;
  /** Function to fetch data for a single model */
  fetchFn: (
    modelId: string,
    scenarioId?: string,
    signal?: AbortSignal
  ) => Promise<T | null>;
  /** Optional: Function to fetch data for multiple models (batch) */
  batchFetchFn?: (
    modelIds: string[],
    scenarioId?: string,
    signal?: AbortSignal
  ) => Promise<Record<string, T>>;
  /** Stale time in ms (default: 5 minutes) */
  staleTime?: number;
  /** Cache time in ms (default: 30 minutes) */
  cacheTime?: number;
  /** Debounce time for batch prefetch (default: 150ms) */
  batchDebounceMs?: number;
  /** Whether scenarioId is required for cache keys */
  requiresScenarioId?: boolean;
  /** Treat cached null values as missing and refetch */
  treatNullAsMissing?: boolean;
}

export interface UseMultiModelDataOptions {
  /** Whether the hook is enabled (default: true) */
  enabled?: boolean;
}

// ============================================================================
// Hook Factory
// ============================================================================

/**
 * Creates a hook for fetching data for multiple models with:
 * - Per-model caching (add/remove doesn't refetch everything)
 * - IndexedDB persistence for worker access
 * - Request cancellation when models are deselected
 * - Debounced batch prefetching
 */
export function createMultiModelDataHook<T>(config: UseMultiModelDataConfig<T>) {
  const {
    dataType,
    queryKeyPrefix,
    fetchFn,
    batchFetchFn,
    staleTime = 5 * 60 * 1000,
    cacheTime = 30 * 60 * 1000,
    batchDebounceMs = 150,
    requiresScenarioId = false,
    treatNullAsMissing = false,
  } = config;

  return function useMultiModelData(
    modelIds: string[],
    scenarioId?: string,
    options?: UseMultiModelDataOptions
  ): MultiModelDataResult<T> {
    const enabled = options?.enabled ?? true;
    const queryClient = useQueryClient();
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
    const currentModelIdsRef = useRef<Set<string>>(new Set());
    const idbInitializedRef = useRef(false);
    const batchAbortControllerRef = useRef<AbortController | null>(null);

    // Memoize modelIds for stable comparison
    const modelIdsKey = useMemo(() => modelIds.slice().sort().join(','), [modelIds]);
    const modelIdsSet = useMemo(() => new Set(modelIds), [modelIdsKey]);

    // Build query key for a model
    const getQueryKey = useCallback(
      (modelId: string) => {
        return requiresScenarioId
          ? [queryKeyPrefix, modelId, scenarioId]
          : [queryKeyPrefix, modelId];
      },
      [scenarioId]
    );

    // Cancel requests for deselected models
    useEffect(() => {
      const previousIds = currentModelIdsRef.current;
      const currentIds = modelIdsSet;

      previousIds.forEach((modelId) => {
        if (!currentIds.has(modelId)) {
          const controller = abortControllersRef.current.get(modelId);
          if (controller) {
            controller.abort();
            abortControllersRef.current.delete(modelId);
            console.log(`[${queryKeyPrefix}] Cancelled request for deselected model: ${modelId}`);
          }
        }
      });

      currentModelIdsRef.current = currentIds;
    }, [modelIdsSet, queryKeyPrefix]);

    const isCacheMissing = useCallback(
      (cached: unknown) => cached === undefined || (treatNullAsMissing && cached === null),
      [treatNullAsMissing]
    );

    // Initialize from IndexedDB on first render with these modelIds
    // Skip if dataType is undefined (some hooks handle IDB separately in batchFetchFn)
    useEffect(() => {
      if (!enabled || modelIds.length === 0 || !dataType) return;

      // Reset initialization flag when modelIds change significantly
      const initKey = `${modelIdsKey}:${scenarioId ?? ''}`;
      const prevInitKey = idbInitializedRef.current;

      if (prevInitKey === true) {
        // Already initialized for some set of models, check if we need to load new ones
        const missingInCache = modelIds.filter(
          (id) => isCacheMissing(queryClient.getQueryData(getQueryKey(id)))
        );

        if (missingInCache.length === 0) return;

        // Load missing models from IDB
        (async () => {
          try {
            const cached = await readMultipleModelData<T>(
              dataType,
              missingInCache,
              requiresScenarioId ? scenarioId : undefined
            );

            if (cached.size > 0) {
              console.log(
                `[${queryKeyPrefix}] Loaded ${cached.size} additional models from IndexedDB`
              );
              cached.forEach((data, modelId) => {
                queryClient.setQueryData(getQueryKey(modelId), data);
              });
            }
          } catch (error) {
            console.warn(`[${queryKeyPrefix}] Error loading from IDB:`, error);
          }
        })();
      } else {
        // First initialization
        idbInitializedRef.current = true;

        (async () => {
          try {
            const cached = await readMultipleModelData<T>(
              dataType,
              modelIds,
              requiresScenarioId ? scenarioId : undefined
            );

            if (cached.size > 0) {
              console.log(`[${queryKeyPrefix}] Initialized ${cached.size} models from IndexedDB`);
              cached.forEach((data, modelId) => {
                queryClient.setQueryData(getQueryKey(modelId), data);
              });
            }
          } catch (error) {
            console.warn(`[${queryKeyPrefix}] Error initializing from IDB:`, error);
          }
        })();
      }
    }, [enabled, modelIdsKey, scenarioId, queryClient, getQueryKey, dataType, queryKeyPrefix]);

    // Debounced batch fetch for models not in cache
    // Uses batch endpoint even for single models when batchFetchFn is available
    useEffect(() => {
      // Debug: log what's happening
      console.log(`[${queryKeyPrefix}] Batch effect running:`, {
        enabled,
        modelIdsCount: modelIds.length,
        modelIds: modelIds.slice(0, 3), // first 3 for brevity
        hasBatchFetchFn: !!batchFetchFn,
        scenarioId,
      });

      if (!enabled) {
        console.log(`[${queryKeyPrefix}] Skipping: not enabled`);
        return;
      }
      if (requiresScenarioId && !scenarioId) {
        console.log(`[${queryKeyPrefix}] Skipping: missing scenarioId`);
        return;
      }
      if (modelIds.length === 0) {
        console.log(`[${queryKeyPrefix}] Skipping: no modelIds`);
        return;
      }
      if (!batchFetchFn) {
        console.log(`[${queryKeyPrefix}] Skipping: no batchFetchFn`);
        return;
      }

      // Check for models missing from cache
      const missingModelIds = modelIds.filter((id) =>
        isCacheMissing(queryClient.getQueryData(getQueryKey(id)))
      );

      if (missingModelIds.length === 0) {
        console.log(`[${queryKeyPrefix}] Skipping: all models already in cache`);
        return;
      }

      console.log(`[${queryKeyPrefix}] Scheduling batch fetch for ${missingModelIds.length} model(s)...`);

      const timeoutId = setTimeout(async () => {
        // Double-check we still need to fetch (cache might have been populated by IDB or another request)
        const stillMissing = missingModelIds.filter((id) =>
          isCacheMissing(queryClient.getQueryData(getQueryKey(id)))
        );

        if (stillMissing.length === 0) {
          console.log(`[${queryKeyPrefix}] All models already cached, skipping fetch`);
          return;
        }

        console.log(`[${queryKeyPrefix}] Batch fetching ${stillMissing.length} model(s)...`);

        // Cancel any previous batch request
        batchAbortControllerRef.current?.abort();
        const batchController = new AbortController();
        batchAbortControllerRef.current = batchController;

        try {
          const batchResult = await batchFetchFn(
            stillMissing,
            scenarioId,
            batchController.signal
          );

          // Update React Query cache and prepare IDB entries
          const idbEntries: Array<{ modelId: string; scenarioId?: string; data: T; }> = [];

          Object.entries(batchResult).forEach(([modelId, data]) => {
            // Only cache if model is still selected
            if (currentModelIdsRef.current.has(modelId)) {
              queryClient.setQueryData(getQueryKey(modelId), data);
              idbEntries.push({
                modelId,
                scenarioId: requiresScenarioId ? scenarioId : undefined,
                data,
              });
            }
          });

          // Write to IndexedDB in background (skip if dataType is undefined)
          if (dataType && idbEntries.length > 0) {
            writeMultipleModelData(dataType, idbEntries).catch((err) =>
              console.warn(`[${queryKeyPrefix}] Error writing batch to IDB:`, err)
            );
          }

          console.log(`[${queryKeyPrefix}] Batch fetch complete, cached ${Object.keys(batchResult).length} model(s)`);
        } catch (error) {
          if ((error as Error).name !== 'AbortError') {
            console.error(`[${queryKeyPrefix}] Batch fetch error:`, error);
          }
        }
      }, batchDebounceMs);

      return () => {
        clearTimeout(timeoutId);
        // Don't abort on cleanup - let in-flight requests complete
        // Only abort if we're switching to different models
      };
    }, [
      modelIdsKey,
      scenarioId,
      enabled,
      batchFetchFn,
      batchDebounceMs,
      queryClient,
      getQueryKey,
      dataType,
      queryKeyPrefix,
      requiresScenarioId,
      isCacheMissing,
    ]);

    // Individual queries with cancellation support
    // Only used when batch fetch is NOT available
    // When batchFetchFn is available, it handles all fetching and populates the React Query cache
    const queries = useQueries({
      queries: modelIds.map((modelId) => {
        return {
          queryKey: getQueryKey(modelId),
          queryFn: async ({ signal }: { signal?: AbortSignal; }): Promise<T | null> => {
            if (!modelId) return null;
            if (requiresScenarioId && !scenarioId) return null;

            // When batchFetchFn exists, individual queries should NOT run
            // The batch effect handles fetching
            if (batchFetchFn) {
              console.warn(`[${queryKeyPrefix}] Individual query running despite batchFetchFn existing - this should not happen`);
            }

            const controller = new AbortController();
            abortControllersRef.current.set(modelId, controller);

            // Link to React Query's signal
            if (signal) {
              signal.addEventListener('abort', () => controller.abort());
            }

            try {
              console.log(`[${queryKeyPrefix}] Individual fetch for model: ${modelId}`);
              const result = await fetchFn(modelId, scenarioId, controller.signal);

              // Write to IndexedDB in background (skip if dataType is undefined)
              if (dataType && result !== null) {
                writeModelData(
                  dataType,
                  modelId,
                  requiresScenarioId ? scenarioId : undefined,
                  result
                ).catch((err) => console.warn(`[${queryKeyPrefix}] Error writing to IDB:`, err));
              }

              return result;
            } finally {
              abortControllersRef.current.delete(modelId);
            }
          },
          // Completely disable individual queries when batchFetchFn is available
          // Batch effect handles all fetching and populates the cache via setQueryData
          enabled: enabled && !!modelId && (!requiresScenarioId || !!scenarioId) && !batchFetchFn,
          staleTime,
          gcTime: cacheTime,
        };
      }),
    });

    // Combine results into a single object keyed by modelId
    const data = useMemo(() => {
      const result: Record<string, T | null> = {};
      modelIds.forEach((modelId, index) => {
        if (queries[index]?.data !== undefined) {
          result[modelId] = queries[index].data;
        }
      });
      return result;
    }, [modelIds, queries]);

    const isLoading = queries.some((q) => q.isLoading);
    const isFetching = queries.some((q) => q.isFetching);
    const isSuccess = queries.length > 0 && queries.every((q) => q.isSuccess);
    const errors = queries.map((q) => q.error);

    const refetchAll = useCallback(async () => {
      await Promise.all(queries.map((q) => q.refetch()));
    }, [queries]);

    const refetchModel = useCallback(
      async (modelId: string) => {
        const index = modelIds.indexOf(modelId);
        if (index !== -1 && queries[index]) {
          await queries[index].refetch();
        }
      },
      [modelIds, queries]
    );

    const invalidateModel = useCallback(
      async (modelId: string) => {
        await queryClient.invalidateQueries({ queryKey: getQueryKey(modelId) });
      },
      [queryClient, getQueryKey]
    );

    return {
      data,
      isLoading,
      isFetching,
      isSuccess,
      errors,
      queries,
      refetchAll,
      refetchModel,
      invalidateModel,
    };
  };
}

